import { LocalVideoTrack, RoomEvent } from 'livekit-client'
import { BgEffect } from '@/src/lib/types'
import { isBackgroundEffectAvailable } from '@/src/lib/detectBackgroundPipeline'
import { BlurSwitcher } from '@/src/components/controls/BlurSwitcher'
import { ImgSwitcher } from '@/src/components/controls/ImgSwitcher'
import { parsBrandingDataDefault } from '@/src/lib/types'
import { CloseIcon } from '@/accets/icons'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRoomContext } from '@/src/context'
import { IBranding } from '@/src/types/meetData.type'
import { initialBranding } from '@/src/constants/branding'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import { useToggleTrackProcessor } from '@/src/hooks/room/track/useToggleTrackProcessor'

export interface VirtualBgHandler extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  track: LocalVideoTrack | undefined
  isPreJoin: boolean
  changeBgEffect: (effect: BgEffect | null) => React.MouseEventHandler<HTMLButtonElement> | void
  /** Элемент превью до входа: обходной путь показывает в нём обработанный стрим. */
  previewVideoRef?: { current: HTMLVideoElement | null }
}

/** Безопасно читает выборы пользователя: битый JSON в localStorage гасится в пусто. */
function readUserChoices(): Record<string, never> | null {
  try {
    const raw = localStorage.getItem('lk-user-choices')
    if (!raw) {
      return null
    }
    return JSON.parse(raw) as Record<string, never>
  } catch {
    return null
  }
}

export function VirtualBgHandler({ track, isPreJoin = true, changeBgEffect, previewVideoRef }: VirtualBgHandler) {
  const { t } = useTranslation()

  const {
    text_primary_color,
    border_radius: borderRadius,
    img_background_speaker,
  } = useAppSelector(selectBranding)
  const [currentBgEffect, setCurrentBgEffect] = useState<BgEffect | null>(
    readUserChoices()?.currentBgEffect ?? null,
  )
  const [userChoices, setUserChoices] = useState<object>(readUserChoices() ?? {})

  useEffect(() => {
    setUserChoices(readUserChoices() ?? {})
  }, [])

  useEffect(() => {
    const setProcessor = async () => {
      await useToggleTrackProcessor(track, currentBgEffect, previewVideoRef?.current)
    }
    if (track) {
      setProcessor()
    }
  }, [currentBgEffect, previewVideoRef])

  useEffect(() => {
    const setProcessor = async () => {
      await useToggleTrackProcessor(track, currentBgEffect, previewVideoRef?.current)
    }
    if (track && isPreJoin) {
      setProcessor()
    }
  }, [track, previewVideoRef])

  useEffect(() => {
    if (userChoices) {
      const newUserChoices = {
        ...userChoices,
        currentBgEffect: currentBgEffect,
      }
      localStorage.setItem('lk-user-choices', JSON.stringify(newUserChoices))
      window.dispatchEvent(new Event('storage'))
    }
    changeBgEffect(currentBgEffect)
  }, [currentBgEffect])

  // На предвходе комнаты ещё нет и провайдера тоже: хук бросает исключение,
  // поэтому забираем комнату безопасно. Подписка ниже сама терпит undefined.
  let room: ReturnType<typeof useRoomContext> | undefined
  try {
    room = useRoomContext()
  } catch {
    room = undefined
  }
  const trackRef = useRef(track)
  trackRef.current = track

  const isProcessorSupported = useMemo(() => isBackgroundEffectAvailable(), [])

  useEffect(() => {
    // Трек публикуется позже превью: после публикации включаем эффект заново,
    // иначе обходной путь захвата останется неактивным.
    const rerun = () => {
      if (trackRef.current) {
        void useToggleTrackProcessor(trackRef.current, currentBgEffect, previewVideoRef?.current)
      }
    }
    room?.on(RoomEvent.LocalTrackPublished, rerun)
    return () => {
      room?.off(RoomEvent.LocalTrackPublished, rerun)
    }
  }, [room, currentBgEffect, previewVideoRef])

  useEffect(() => {
    // Элемент копируем сразу: в cleanup читать ref уже нельзя.
    const previewVideo = previewVideoRef?.current ?? null
    // Размонтирование: гасим эффект и возвращаем оригинальный трек камеры.
    return () => {
      const currentTrack = trackRef.current
      if (currentTrack) {
        void useToggleTrackProcessor(currentTrack, { type: 'none', options: {} }, previewVideo)
      }
    }
  }, [previewVideoRef])

  const handleBgEffect = useCallback((type: BgEffect) => {
    setCurrentBgEffect(type)
  }, [])

  return (
    isProcessorSupported && (
      <div className="lk-bg-effects">
        <div className="lk-bg-effects-title">
          <p
            style={{
              color: text_primary_color,
            }}
          >
            {t('changeBg')}:
          </p>
        </div>

        <button
          aria-label="Delete blur effect"
          className={[
            'lk-button-bg-effects',
            currentBgEffect?.type === 'none' || !currentBgEffect ? 'active' : '',
          ].join(' ')}
          style={{
            borderColor:
              currentBgEffect?.type === 'none' || !currentBgEffect
                ? text_primary_color
                : 'transparent',
            borderRadius: borderRadius,
          }}
          onClick={() => setCurrentBgEffect({ type: 'none', options: {} })}
        >
          <CloseIcon stroke="red" fill={text_primary_color} width={36} height={36} />
        </button>

        <BlurSwitcher
          currentBgEffect={currentBgEffect}
          track={track}
          setBgEffect={handleBgEffect}
        />

        {img_background_speaker && (
          <ImgSwitcher
            currentBgEffect={currentBgEffect}
            track={track}
            setBgEffect={handleBgEffect}
          />
        )}
      </div>
    )
  )
}
